import * as React from 'react';

import { Backdrop, Box, CircularProgress, CardProps as MuiCardProps } from '@mui/material';

import { theme } from '@itp/talent/theme';

import { ActionArea, ActionAreaProps } from './actionArea';
import { Actions, ActionsProps } from './actions';
import { StyledCard } from './card.styles';
import { Content, ContentProps } from './content';
import { Header, HeaderProps } from './header';

export interface CardProps extends MuiCardProps {
  children: React.ReactNode;
  loading?: boolean;
  noPadding?: boolean;
  noShadow?: boolean;
  selected?: boolean;
}

interface Composition {
  ActionArea: React.FC<ActionAreaProps>;
  Actions: React.FC<ActionsProps>;
  Content: React.FC<ContentProps>;
  Header: React.FC<HeaderProps>;
}

function hasDataTableDescendant(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false;
    if ((child.type as any)._isDataTable === true) return true;
    return hasDataTableDescendant((child.props as any)?.children);
  });
}

export const Card: React.FC<CardProps> & Composition = ({
  children,
  loading,
  noPadding,
  sx,
  ...rest
}) => {
  const shouldRemovePadding = noPadding ?? hasDataTableDescendant(children);

  return (
    <StyledCard
      {...rest}
      sx={{
        p: shouldRemovePadding ? 0 : { sm: 4, xs: 2 },
        ...sx,
      }}
      loading={loading}
      variant="outlined"
    >
      <Box height="100%" position="relative">
        {loading && (
          <Backdrop
            sx={{
              background: 'rgba(255,255,255,0.5)',
              color: theme.palette.primary.main,
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        {children}
      </Box>
    </StyledCard>
  );
};

Card.Content = Content;
Card.Header = Header;
Card.Actions = Actions;
Card.ActionArea = ActionArea;
